import { ModeEditOutlined as ModeEditOutlinedIcon, PencilIcon } from 'components/icons';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import { Button } from 'components/ui/button';
import { useState } from 'react';
import { Control, useWatch } from 'react-hook-form';
import { useSelector } from 'store';
import { InvoiceExpense, InvoiceMatterForm } from 'types/invoice-details';
import { cn } from 'utils/cn';
import { InvoiceDetailsSimpleTracker } from '../../InvoiceDetailsChangeTrackers';

type Props = {
    isRowDisabled: boolean;
    readonly: boolean;
    invoiceExpense: InvoiceExpense;
    index: number;
    control: Control<InvoiceMatterForm>;
    onSubmit: () => void;
    /** The new design's name for the edit button beside the text ("Edit description, {date}, {name}"). */
    editLabel?: string;
};

const ModeEditIcon = ({ className }: { className?: string }) => (
    <ModeEditOutlinedIcon className={cn('text-grey-500', className)} fontSize="small" />
);

export const DescriptionDisplay = ({
    index,
    control,
    onSubmit,
    invoiceExpense,
    isRowDisabled,
    readonly,
    editLabel,
}: Props) => {
    const [isEditMode, setIsEditMode] = useState(false);
    const { expense } = invoiceExpense;
    const [description] = useWatch({ control, name: [`invoiceExpenses.${index}.description`] });
    const { buttonsDisabled: areButtonsDisabled } = useSelector((state) => state.buttons);

    return (
        <div className="opacity-decreasing-when-disabled flex flex-col">
            {editLabel && (isRowDisabled || !isEditMode) ? (
                <p className="text-body1 m-0 mt-1.5 max-w-[72ch] text-pretty wrap-anywhere">
                    {description}
                    {readonly || isRowDisabled ? null : (
                        <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={editLabel}
                            disabled={areButtonsDisabled}
                            onClick={() => setIsEditMode(true)}
                            className="text-foreground-tertiary -my-1.5 ms-1 align-middle"
                        >
                            <PencilIcon />
                        </Button>
                    )}
                </p>
            ) : isRowDisabled || !isEditMode ? (
                <div
                    className={cn(
                        'flex cursor-pointer justify-between',
                        (readonly || isRowDisabled) && 'pointer-events-none'
                    )}
                    onClick={() => (areButtonsDisabled ? undefined : setIsEditMode(true))}
                >
                    <div className="min-h-5">{description}</div>
                    {readonly || isRowDisabled ? null : <ModeEditIcon />}
                </div>
            ) : (
                <TextInput
                    control={control}
                    name={`invoiceExpenses.${index}.description`}
                    onBlur={() => {
                        setIsEditMode(false);
                        if (description !== invoiceExpense.description) {
                            onSubmit();
                        }
                    }}
                    multiline
                    readonly={readonly}
                    autoFocus
                    gap={0}
                    suffixComponent={<ModeEditIcon className="absolute top-[5px] right-[5px] size-5" />}
                />
            )}
            {expense?.description && expense?.description.length > 0 && (
                <InvoiceDetailsSimpleTracker
                    currentValue={description ? description : undefined}
                    storedValue={expense?.description}
                />
            )}
        </div>
    );
};
